import React, { useEffect } from 'react';
import {
  Button,
  Modal,
  Text,
  ButtonGroup,
  Grid,
  Input,
  Stack,
  Shelf,
  Tooltip,
  Position,
} from '@chaser/components';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import format from 'date-fns/format';
import pluralize from 'pluralize';

import CopyButton from '../../../../../components/CopyButton';
import OpenApiService from '../../../../../services/openapi-service';
import usePush from '../../../../../hooks/usePush';
import useFetch from '../../../../../hooks/useFetch';
import useOrganisation from '../../../../../hooks/useOrganisation';
import useUser from '../../../../../hooks/useUser';

function RequestAPITokenModal({ open, close, onClosed, ShortCode }) {
  const { data: apiKeys, upsert, loading: loadingKeys } = useFetch(
    OpenApiService.getApiKeys,
    ShortCode,
  );
  const { execute: generateKeys, loading: loadingGenerateKeys } = usePush(
    OpenApiService.generateApiKey,
  );
  const { execute: recycleKeys, loading: loadingNewKey } = usePush(OpenApiService.recycleApiKeys);

  const { lastSyncDate, lastRefreshDate, createdAt, manager } = useOrganisation();

  const loading = loadingKeys || loadingGenerateKeys;

  useEffect(() => {
    async function generate() {
      const [err, res] = await generateKeys(ShortCode);
      if (err) return;

      upsert(state => {
        state.key = res.key;
        state.secret = res.secret;
        state.createdAt = res.createdAt;
      });
    }

    const hasKeys = apiKeys && apiKeys.key && apiKeys.secret;
    if (!hasKeys) {
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKeys]);

  async function recycle(ShortCode, key) {
    const [err, res] = await recycleKeys(ShortCode, key);
    if (err) return;

    upsert(state => {
      state.key = res.key;
      state.secret = res.secret;
      state.createdAt = res.createdAt;
    });
  }

  function ApiDetailRow({ label, value, loading = false }) {
    return (
      <Grid
        columns={2}
        rowGap="medium"
        alignItems="center"
        gridTemplateColumns="50px auto"
        width="100%"
      >
        <Text>{label}</Text>
        <Input
          readonly
          backgroundColor="Light Neutral 3"
          iconRight={!!value && <CopyButton value={value} />}
          disabled
          value={loading ? 'Loading...' : value}
          width="100%"
        />
      </Grid>
    );
  }

  function getDays(date) {
    return Math.abs(differenceInCalendarDays(new Date(date), new Date()));
  }

  function getTime(date) {
    return format(date, 'HH:mm');
  }

  const lastRefreshDays = getDays(apiKeys?.createdAt);
  const lastSyncDays = getDays(lastSyncDate || lastRefreshDate || createdAt);
  const hasInfoDate = lastRefreshDays || lastSyncDays;
  const lastSyncTime = lastSyncDate ? getTime(new Date(lastSyncDate)) : '';

  const syncLabel = `Last synced: ${
    lastSyncDays === 0 ? `Today at ${lastSyncTime}` : `${pluralize('day', lastSyncDays, true)} ago`
  }`;

  const currentUser = useUser();
  const isOwner = manager && currentUser && currentUser.userId === manager._id;

  return (
    <Modal open={open} title="Your Chaser API keys" toggle={close} onClosed={onClosed}>
      <Modal.Body>
        <Stack gap="large">
          <Stack gap="xsmall">
            <Text>
              This unique key gives developers full access to your organisation in Chaser.
            </Text>
            <Text fontWeight="bold">Keep it safe and don’t share it publically.</Text>
          </Stack>
          <Stack dividers>
            <ApiDetailRow label="Host" value="openapi.chaserhq.com" />
            <ApiDetailRow label="Key" value={apiKeys?.key} loading={loading} />
            <ApiDetailRow label="Secret" value={apiKeys?.secret} loading={loading} />
            <div />
          </Stack>
          <Shelf gap="small">
            <Tooltip
              position={Position.BOTTOM}
              content={syncLabel}
              disable={!lastSyncDays && lastSyncDate !== 0}
            >
              <Button
                loading={loadingNewKey}
                onClick={() => recycle(ShortCode, apiKeys?.key)}
                primary
                type="submit"
                appearance="secondary"
                size="narrow"
                disabled={!isOwner}
              >
                Refresh keys
              </Button>
            </Tooltip>
            {(hasInfoDate === 0 || hasInfoDate) && apiKeys?.createdAt && (
              <Text color="Mid Neutral 1">
                Last refreshed:{' '}
                {lastRefreshDays === 0 ? 'Today' : `${pluralize('day', lastRefreshDays, true)} ago`}
              </Text>
            )}
          </Shelf>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={() => close(true)}>Done</Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
}

export default RequestAPITokenModal;
